import React, { useState, useContext, useRef, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import DocumentTable from "../components/document-table/DocumentTable";
import PdfViewer from "../components/pdf-viewer/PdfViewer";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import "../styles/Documents.css";
import { useRoleContext } from "../context/RolesContext";
import { AuthContext } from "../context/Auth/AuthContext";
import documentData from "../api/documents";
import baseUrl from "../config/baseUrl";
import { showError } from "../utils/toast";
import PageInfoTooltip from "../components/common/PageInfoTooltip";

const getSectionTitle = (clientID) =>
  (clientID === "not null" && "Processed Documents") ||
  (clientID === "null" && "Unprocessed Documents") ||
  (clientID === "client unknown" && "Client Unknown Documents") ||
  "All Documents";

const getSectionPath = (clientID) =>
  (clientID === "not null" && "/processed-documents") ||
  (clientID === "null" && "/unprocessed-documents") ||
  (clientID === "client unknown" && "/client-unknown-documents") ||
  "/documents";

const getSectionDescription = (clientID) =>
  (clientID === "not null" && "Documents that have already been processed") ||
  (clientID === "null" && "Documents that are pending processing") ||
  (clientID === "client unknown" &&
    "Documents that could not be matched to a client") ||
  "All documents in the system";

/*** Main component for listing the document table & for showing the 'Add Documents' button.
 * Opening a document navigates to its Document Record/PDF Viewer in place of the list
 * (breadcrumb-only navigation - no internal multi-document tab strip). Use the browser's
 * own tabs/new window for viewing multiple documents at once. ***/
const Documents = ({ clientID }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assignUserId = queryParams.get("assignUser");

  const tableRef = useRef(null);
  const [documentDetails, setDocumentDetails] = useState(null);
  const [updatedFromPDF, setUpdatedFromPDF] = useState(false);
  const [showAddDocumentForm, setShowAddDocumentForm] = useState(false);

  // Unprocessed/Processed/Client Unknown/All Documents are all separate
  // routes rendering this same component with a different clientID prop, so
  // React Router doesn't unmount/remount it on navigation between them (same
  // component type, same position in the tree) - only the props change.
  // Without this, an open document's local state survived the navigation
  // and kept rendering PdfViewer instead of the newly-routed-to list.
  // Resetting during render (not in a useEffect) so the stale document
  // never paints for even one frame - the list shows immediately on the
  // first click, exactly like a fresh mount would behave.
  const [clientIDForReset, setClientIDForReset] = useState(clientID);
  if (clientID !== clientIDForReset) {
    setClientIDForReset(clientID);
    setDocumentDetails(null);
    setShowAddDocumentForm(false);
    setUpdatedFromPDF(false);
  }

  const { is_admin, token, id: userId } = useContext(AuthContext);
  const navigate = useNavigate();

  // Deep link from the Audit Trail ("Open Document"): /documents?openDocument=<id>
  // loads that one row with the existing get-all-doc-data API and opens it
  // exactly as clicking it in the list does. Does nothing without the param.
  const openDocumentId = queryParams.get("openDocument");
  useEffect(() => {
    if (!openDocumentId) return;
    let active = true;
    const params = new URLSearchParams({
      document_id: openDocumentId,
      isAdmin: is_admin,
      userId,
      ...(is_admin === "1" && localStorage.getItem("role_id") ? { roleId: localStorage.getItem("role_id") } : {}),
    });
    documentData({ token, api: `${baseUrl}/api/get-all-doc-data?${params.toString()}` }).then((res) => {
      if (!active) return;
      const doc = (res?.data?.[0]?.document || []).find((d) => d.document_id === openDocumentId);
      if (doc) {
        setUpdatedFromPDF(false);
        setDocumentDetails(doc);
      } else {
        showError("This document could not be opened. It may have been deleted or you may not have access to it.");
      }
      navigate(location.pathname, { replace: true });
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDocumentId]);
  const { permissions } = useRoleContext();
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const refetchDocumentTable = () => {
    setRefetchTrigger((prev) => prev + 1);
  };
  const hasPermission = (permission) => {
    if (is_admin === "1") return true;
    return permissions?.includes(permission);
  };

  // Opening a document replaces the list view with its Document Record/PDF
  // Viewer - there's only ever one "open document" at a time now.
  const addDocumentTab = (document) => {
    setUpdatedFromPDF(false);
    setDocumentDetails(document);
  };

  // "Close" now just means "go back to the list" - no tab array/IDs to
  // manage. Kept as closeTabById(id) / currentTabId so the existing
  // Approve/Reject/LinkToRequest popups (which call
  // `if (currentTabId) closeTabById(currentTabId)` after their action)
  // don't need to change.
  const closeTabById = () => {
    setDocumentDetails(null);
  };
  const currentTabId = documentDetails?.document_id || null;

  // If the document currently open gets deleted from the list, return to
  // the list instead of continuing to show a now-nonexistent document.
  const checkDocumentOpen = (documentId) => {
    if (documentDetails?.document_id === documentId) {
      setDocumentDetails(null);
    }
  };

  const onAddDocumentButtonClick = () => {
    setShowAddDocumentForm(!showAddDocumentForm);
  };

  const sectionTitle = getSectionTitle(clientID);
  const openDocumentName =
    documentDetails?.update_document_name || documentDetails?.document_name;

  return (
    <div className="document-container">
      <div className="navbar">
        <Navbar />
      </div>
      <div
        className="document-content documents-zoom-scroll-scope"
        ref={tableRef}
        style={{
          scrollBehavior: "smooth",
          scrollTimingFunction: "ease-in-out",
        }}
      >
        {/* Breadcrumb - only for the open-document view (deeper content).
            The list view has no breadcrumb: the Sidebar already makes the
            current section clear. */}
        {documentDetails && (
          <div className="documents-breadcrumb flex items-center gap-1 text-sm text-gray-500 shrink-0">
            <Link
              to={getSectionPath(clientID)}
              onClick={closeTabById}
              className="hover:text-nbl-primary hover:underline"
            >
              {sectionTitle}
            </Link>
            <ChevronRightIcon sx={{ fontSize: 16 }} />
            <span className="text-gray-900 font-medium truncate max-w-[280px]">
              {openDocumentName}
            </span>
          </div>
        )}

        {/* Title + description + Add Document - list view only; an open
            document has its own header inside PdfViewer. Sits directly above
            the KPI tiles (rendered inside DocumentTable) with matching
            horizontal padding and no gap, so they read as one header area. */}
        {!documentDetails && !showAddDocumentForm && (
          <div className="documents-page-header flex items-center justify-between gap-3 py-3 shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-nbl-primary text-white">
                <ArticleOutlinedIcon sx={{ fontSize: 18 }} />
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 min-w-0">
                  <h1 className="text-lg font-semibold text-gray-900 leading-tight truncate min-w-0">
                    {sectionTitle}
                  </h1>
                  <PageInfoTooltip title={sectionTitle} description={getSectionDescription(clientID)} />
                </span>
                <p className="text-xs text-gray-500 leading-tight truncate">
                  {getSectionDescription(clientID)}
                </p>
              </span>
            </div>
            {hasPermission("create_all_documents") && (
              <button
                className="add-document shrink-0"
                onClick={onAddDocumentButtonClick}
              >
                <NoteAddIcon sx={{ fontSize: 18 }} />
                Add Document
              </button>
            )}
          </div>
        )}

        <div className="tab-content">
          {documentDetails ? (
            <DndProvider backend={HTML5Backend}>
              <PdfViewer
                documentType={documentDetails.document_type}
                clientID={documentDetails.contact_id}
                documentID={documentDetails.document_id}
                documentTitle={documentDetails.update_document_name}
                documentCreatedAt={documentDetails.created_at}
                documentURL={documentDetails.document_url}
                changeActiveTab={closeTabById}
                document_table="documents"
                document_version={documentDetails.document_version}
                documentRequestID={documentDetails.document_request_id}
                existingDocumentRequestStatus={
                  documentDetails.document_request_status
                }
                documentCategory={documentDetails.doc_category}
                documentDesc={documentDetails.document_desc}
                currentTabId={currentTabId}
                closeTabById={closeTabById}
                setDocumentDetails={setDocumentDetails}
                fromTable="Documents"
                setUpdatedFromPDF={setUpdatedFromPDF}
                linkedDocReqName={documentDetails.document_request_setting_name}
                documentSubCategory={documentDetails.document_sub_category}
                clientPhone={documentDetails.phone_number}
                benID={documentDetails.ben_id}
                refetchDocumentTable={refetchDocumentTable}
                refetchTrigger={refetchTrigger}
              />
            </DndProvider>
          ) : (
            <DocumentTable
              key={clientID}
              tableRef={tableRef}
              addDocumentTab={addDocumentTab}
              showAddDocumentForm={showAddDocumentForm}
              onAddDocumentButtonClick={onAddDocumentButtonClick}
              clientID={clientID}
              checkDocumentOpen={checkDocumentOpen}
              assignUserId={assignUserId}
              refetchTrigger={refetchTrigger}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Documents;
